import React, { useState, useRef, useEffect } from 'react';
import { chatAPI, userAPI } from '../../services/api';
import MessageBubble from './MessageBubble';
import ChatHistory from './ChatHistory';
import ResultDisplay from './ResultDisplay';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  PaperAirplaneIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  DocumentArrowUpIcon,
  ChevronDownIcon,
  ChartBarIcon,
  PresentationChartBarIcon,
  PlayIcon,
  CheckCircleIcon,
  XMarkIcon,
  TableCellsIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FolderIcon,
  ChartPieIcon,
  CpuChipIcon,
  UserIcon,
  SparklesIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  WalletIcon,
  CodeBracketIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { Listbox } from '@headlessui/react';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from "uuid";

const modules = [
  // --- Acceptance Models: hidden for now (still under development). ---
  // Keep this block so it can be re-enabled later by uncommenting it.
  // {
  //   id: 'acceptance',
  //   name: 'Acceptance Models',
  //   icon: PresentationChartBarIcon,
  //   description: 'Upload and analyze CSV files for acceptance testing',
  //   color: 'blue',
  //   storageKey: 'acceptance_file_data'
  // },
  {
    id: 'utilization',
    name: '100-Offer Acceptance Model',
    icon: ChartBarIcon,
    description: 'Predict whether a kidney will be accepted within 100 offers',
    color: 'green',
    storageKey: 'utilization_file_data'
  }
];

// Bundled donor-only sample organs (served from /public/samples). Each file holds
// just the donor fields the utilization model reads — no candidate/match-run
// columns. "Positive" = actually accepted within 100 offers; "high confidence" =
// the donor's KDPI-based usability agrees with that outcome, "low" = it doesn't.
const sampleOrgans = [
  {
    label: 'High-confidence positive',
    path: '/samples/sample_organ_high_conf_positive.csv',
    file: 'Sample Organ - High-Confidence Positive.csv'
  },
  {
    label: 'Low-confidence positive',
    path: '/samples/sample_organ_low_conf_positive.csv',
    file: 'Sample Organ - Low-Confidence Positive.csv'
  },
  {
    label: 'High-confidence negative',
    path: '/samples/sample_organ_high_conf_negative.csv',
    file: 'Sample Organ - High-Confidence Negative.csv'
  },
  {
    label: 'Low-confidence negative',
    path: '/samples/sample_organ_low_conf_negative.csv',
    file: 'Sample Organ - Low-Confidence Negative.csv'
  }
];

const ChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const [selectedModule, setSelectedModule] = useState(modules[0]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedData, setUploadedData] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [moduleResult, setModuleResult] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [dragActive, setDragActive] = useState(false);
  const [originalFile, setOriginalFile] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showDataPreview, setShowDataPreview] = useState(true);
  const [openAccordion, setOpenAccordion] = useState(0);
  const [isFromHistory, setIsFromHistory] = useState(false);
  const [selectedChatData, setSelectedChatData] = useState(null);
  const [isChatDisabled, setIsChatDisabled] = useState(false);
  const [showChatbot, setShowChatbot] = useState(true);
  const [chatload, setchatload] = useState(false);
  const [availableTokens, setAvailableTokens] = useState(0);
  const [minToken, setMinToken] = useState(10000); // Default min token
  const [tokenLoading, setTokenLoading] = useState(true);
  const [showNoTokenModal, setShowNoTokenModal] = useState(false);
  const [toolLogs, setToolLogs] = useState([]);

  const currentUser = JSON.parse(localStorage.getItem('user')) || {};
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const rowsPerPage = 5;

  const modelApiUrl = process.env.REACT_APP_PYTHON_API_URL

  // Fetch user tokens on component mount
  useEffect(() => {
    fetchUserTokens();
  }, []);

  const fetchUserTokens = async () => {
    setTokenLoading(true);
    try {
      const response = await userAPI.getUserTokens();
      const tokenData = response.data || {};
      const available = tokenData.available_token || 0;
      const min = tokenData.min_token || 10000;
      
      setAvailableTokens(available);
      setMinToken(min);
      
      // Check if available tokens are less than minimum required
      if (available < min) {
        setIsChatDisabled(true);
        setShowNoTokenModal(true);
      } else {
        setIsChatDisabled(false);
      }
    } catch (error) {
      console.error('Failed to fetch tokens:', error);
      setAvailableTokens(0);
      setMinToken(10000);
      setIsChatDisabled(true);
      setShowNoTokenModal(true);
    } finally {
      setTokenLoading(false);
    }
  };

  const checkAndDeductToken = async () => {
    if (availableTokens < minToken) {
      toast.error(`You need at least ${minToken.toLocaleString()} tokens to continue. Please contact admin to add more tokens.`);
      setShowNoTokenModal(true);
      return false;
    }
    return true;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // A conversation opened from the sidebar can be carried on: the backend
  // restores the case (and the assistant's own transcript) from the durable
  // store when Redis has gone cold, so it no longer has to be read-only.
  const isResumedHistory = isFromHistory && !!selectedChatData?.session_id;

  // Whether the chat panel has anything to show. A resumed conversation counts
  // even with no turns and no stored result: sessions get a placeholder row the
  // moment one is opened, so a first message that never completed leaves an
  // empty conversation in the sidebar. Without this, opening one rendered a
  // blank pane — no transcript, no composer, nothing to click.
  const hasChatContent = moduleResult || messages.length > 0 || isResumedHistory;

  const isChatEnabled =
    (moduleResult !== null || isResumedHistory) && !isChatDisabled && availableTokens >= minToken;

  const handleModuleChange = (module) => {
    if (isFromHistory) {
      toast.error('Cannot change module while viewing history');
      return;
    }
    setSelectedModule(module);
    setModuleResult(null);
    setCurrentPage(1);
    if (!isFromHistory) {
      resetAll();
    }
  };

  const resetAll = () => {
    setUploadedFile(null);
    setUploadedData(null);
    setModuleResult(null);
    setUploadProgress(0);
    setCurrentPage(1);
    setMessages([]);
    setIsFromHistory(false);
    setSelectedChatData(null);
    setIsChatDisabled(false);
    setShowChatbot(true);
    setOriginalFile(null);
    setToolLogs([]);

    // Drop the session pointer too, so starting fresh after resuming an old
    // conversation cannot attach the new one to that conversation's session.
    // The next upload mints a new id immediately after calling this.
    localStorage.removeItem('session_id');

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDrag = (e) => {
    if (isFromHistory) return;
    if (availableTokens < minToken) {
      toast.error(`You need at least ${minToken.toLocaleString()} tokens to upload files.`);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    if (isFromHistory) {
      toast.error('Cannot upload file while viewing history');
      return;
    }
    if (availableTokens < minToken) {
      toast.error(`You need at least ${minToken.toLocaleString()} tokens to upload files.`);
      setShowNoTokenModal(true);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleFileIconClick = () => {
    if (isFromHistory) {
      toast.error('Cannot upload file while viewing history');
      return;
    }
    if (availableTokens < minToken) {
      toast.error(`You need at least ${minToken.toLocaleString()} tokens to upload files.`);
      setShowNoTokenModal(true);
      return;
    }
    fileInputRef.current?.click();
  };

  // Load a bundled sample case (served from /public/samples) and run it through
  // the normal upload flow so the user can try the model with one click.
  const loadSample = async (path, name) => {
    if (availableTokens < minToken) {
      toast.error(`You need at least ${minToken.toLocaleString()} tokens to run the model.`);
      setShowNoTokenModal(true);
      return;
    }
    try {
      const res = await fetch(path);
      if (!res.ok) throw new Error('fetch failed');
      const text = await res.text();
      const sampleFile = new File([text], name, { type: 'text/csv' });
      await handleFileSelect(sampleFile);
      toast.success('Sample loaded — click Run Model to see predictions');
    } catch (e) {
      toast.error('Could not load sample data');
    }
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    if (availableTokens < minToken) {
      toast.error(`You need at least ${minToken.toLocaleString()} tokens to upload files.`);
      setShowNoTokenModal(true);
      return;
    }

    const lowerName = file.name.toLowerCase();
    const isCsv = file.type === 'text/csv' || lowerName.endsWith('.csv');
    const isPdf = file.type === 'application/pdf' || lowerName.endsWith('.pdf');

    if (!isCsv && !isPdf) {
      toast.error('Only CSV or DonorNet PDF files are allowed');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File exceeds the 10MB limit');
      return;
    }

    // Identity for this upload — the same session id is used for the PDF
    // redaction/S3 step and the Node upload, so the stored S3 object and the
    // uploaded_files row are linked to the same user + session.
    const uploadUser = JSON.parse(localStorage.getItem("user") || "null");
    const uid = uuidv4();
    let s3Key = null;

    // A DonorNet PDF is converted to the case CSV server-side first (and the
    // de-identified copy is stored to S3 under this user/session), then the
    // rest of the flow (preview + run) proceeds exactly as it does for a CSV.
    if (isPdf) {
      setUploading(true);
      setUploadProgress(0);
      try {
        const pdfForm = new FormData();
        pdfForm.append('file', file);
        if (uploadUser?.id) pdfForm.append('user_id', String(uploadUser.id));
        pdfForm.append('session_id', uid);
        const conv = await axios.post(`${modelApiUrl}/utilization/parse_pdf`, pdfForm);
        if (!conv.data || conv.data.status !== 'success' || !conv.data.csv) {
          throw new Error(conv.data?.message || 'PDF parse failed');
        }
        s3Key = conv.data.s3_key || null;
        const csvName = conv.data.filename || file.name.replace(/\.pdf$/i, '.csv');
        file = new File([conv.data.csv], csvName, { type: 'text/csv' });
        toast.success('PDF converted to case data');
      } catch (err) {
        setUploading(false);
        toast.error('Could not read this PDF. Please try a CSV instead.');
        return;
      }
    }

    resetAll();
    setOriginalFile(file);
    setUploading(true);
    setUploadProgress(0);
    localStorage.setItem("session_id", uid);
    const formData = new FormData();
    formData.append('file', file);
    if (s3Key) formData.append('s3_key', s3Key);

    try {
      const response = await chatAPI.uploadFile(
        formData,
        (progressEvent) => {
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percent);
        }
      );

      if (response.data.success) {
        setchatload(false);

        const fileInfo = {
          name: response.data.file.original_name,
          size: response.data.file.file_size
        };

        const parsedData = {
          headers: response.data.data.headers,
          rows: response.data.data.rows,
          totalRows: response.data.data.totalRows,
          totalColumns: response.data.data.totalColumns
        };

        setUploadedFile(fileInfo);
        setUploadedData(parsedData);
      }
    } catch (error) {
      toast.error(error.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSelectChat = async (chat) => {
    setMessages([]);
    setModuleResult(null);
    setUploadedData(null);
    setUploadedFile(null);
    setToolLogs([]);

    setSelectedChatData(chat);
    setIsFromHistory(true);

    // Point the session at this conversation so follow-up messages land on it.
    // The ML backend keys its Redis state (and the durable restore) on this id,
    // so without it a resumed chat would silently start a new, empty session.
    if (chat.session_id) {
      localStorage.setItem('session_id', chat.session_id);
    }

    // Keep the conversation on the model it was actually run through.
    const chatModuleId = chat.model || chat.messages?.[0]?.module;
    const chatModule = modules.find((m) => m.id === chatModuleId);
    if (chatModule) setSelectedModule(chatModule);

    // Resumable, so leave the composer live rather than locking it.
    setIsChatDisabled(!chat.session_id);
    setShowChatbot(true);

    const chatMessages = [];

    if (Array.isArray(chat.messages)) {
      chat.messages.forEach((msg, index) => {
        if (msg.question) {
          chatMessages.push({
            id: `${chat.id}_${index}_user`,
            message: msg.question,
            created_at: msg.timestamp,
            isUser: true,
            isTyping: false
          });
        }

        if (msg.answer) {
          chatMessages.push({
            id: `${chat.id}_${index}_bot`,
            message: msg.answer,
            created_at: msg.timestamp,
            isUser: false,
            isTyping: false,
            shapPlot: msg.shap_plot || null
          });
        }
      });
    }

    setMessages(chatMessages);

    if (chat.uploaded_data) {
      setUploadedData(chat.uploaded_data);
      setUploadedFile({
        name: chat.uploaded_data.file_name || "Uploaded file",
        size: chat.uploaded_data.file_size || 0
      });
    }

    if (chat.module_result) {
      setModuleResult({ data: chat.module_result });
    }

    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  const handleRunModule = async () => {
    if (isFromHistory) {
      toast.error('Cannot run module while viewing history');
      return;
    }

    if (availableTokens < minToken) {
      toast.error(`You need at least ${minToken.toLocaleString()} tokens to run the module.`);
      setShowNoTokenModal(true);
      return;
    }

    const session_id = localStorage.getItem("session_id");
    const user = JSON.parse(localStorage.getItem("user"));
    const createFormData = () => {
      const fd = new FormData();
      fd.append("file", originalFile);
      fd.append("selected_index", 0);
      fd.append("session_id", session_id);
      fd.append("user_id", user.id);
      return fd;
    };

    setProcessing(true);

    try {
      let response;
      if (selectedModule.id === "acceptance") {
        response = await axios.post(
          `${modelApiUrl}/acceptance/run`,
          createFormData()
        );
      } else if (selectedModule.id === "utilization") {
        response = await axios.post(
          `${modelApiUrl}/utilization/run`,
          createFormData()
        );
      }

      console.log(response.data);
      setModuleResult(response);

      // Refresh token count after module run (tokens were used)
      await fetchUserTokens();

    } catch (error) {
      console.error(error);
      toast.error("API failed");
    } finally {
      setProcessing(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || isSending || !isChatEnabled) return;

    // Check if user has tokens before sending
    const hasTokens = await checkAndDeductToken();
    if (!hasTokens) return;

    const userMessage = {
      id: Date.now(),
      message: input,
      created_at: new Date().toISOString(),
      isUser: true,
      isTyping: false,
      selectedModule: selectedModule.id
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsSending(true);

    const botMessageId = Date.now() + 1;
    const botMessage = {
      id: botMessageId,
      message: '',
      created_at: new Date().toISOString(),
      isUser: false,
      isTyping: true,
    };

    setMessages(prev => [...prev, botMessage]);

    setTimeout(scrollToBottom, 50);

    try {
      const result = await chatAPI.sendMessage({
        message: userMessage.message,
        selectedModule: selectedModule.id
      });

      // The ML backend could not restore this session (nothing durable was
      // stored for it — e.g. a conversation from before case persistence).
      // Surface that as a readable message instead of rendering the payload.
      if (result.data.response?.status === 'expired') {
        setMessages(prev => prev.map(msg =>
          msg.id === botMessageId
            ? {
              ...msg,
              message: result.data.response.message
                || 'This conversation can no longer be continued. Please re-upload the case file.',
              isTyping: false
            }
            : msg
        ));
        setIsChatDisabled(true);
        setIsSending(false);
        return;
      }

      if (result.data.success) {
        console.log(result.data);
        const fullResponse = result.data.response.answer || result.data.response;

        // Extract tool logs from response
        if (result.data.response.tool_logs && Array.isArray(result.data.response.tool_logs)) {
          const newToolLogs = result.data.response.tool_logs.map(log => ({
            ...log,
            timestamp: log.timestamp || new Date().toISOString()
          }));
          setToolLogs(prev => [...prev, ...newToolLogs]);
        }

        // SHAP plot rendered inline when the assistant invoked show_shap_plot.
        const shapPlot = result.data.response.shap_plot || null;

        setMessages(prev => prev.map(msg =>
          msg.id === botMessageId
            ? { ...msg, message: fullResponse, isTyping: true, shapPlot }
            : msg
        ));

        const typingDuration = Math.min(fullResponse.length * 25, 5000);
        setTimeout(() => {
          setMessages(prev => prev.map(msg =>
            msg.id === botMessageId ? { ...msg, isTyping: false } : msg
          ));
          setIsSending(false);
          // Refresh token count after message
          fetchUserTokens();
        }, typingDuration);

      } else {
        setMessages(prev => prev.map(msg =>
          msg.id === botMessageId
            ? { ...msg, message: result.data.message, isTyping: false }
            : msg
        ));
        setIsSending(false);
      }
    } catch (error) {
      console.error('Send message error:', error);
      // A non-2xx reply (e.g. 502 when the model service fails) rejects here
      // with the server's explanation attached. Show that rather than calling
      // every failure a network error — only a genuinely absent response is one.
      const serverMessage =
        error.response?.data?.response?.message || error.response?.data?.message;
      setMessages(prev => prev.map(msg =>
        msg.id === botMessageId
          ? {
            ...msg,
            message: serverMessage || "Network error. Please try again.",
            isTyping: false
          }
          : msg
      ));
      setIsSending(false);
    }
  };

  const totalPages = uploadedData?.rows ? Math.ceil(uploadedData.rows.length / rowsPerPage) : 0;
  const paginatedRows = uploadedData?.rows ?
    uploadedData.rows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage) : [];

  // Format file size helper
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // No Token Modal - Updated message based on token status
  const NoTokenModal = () => {
    const needsTokens = availableTokens < minToken;
    const hasZeroTokens = availableTokens === 0;
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-2xl p-6 max-w-md mx-4 shadow-xl">
          <div className="text-center">
            <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-4 ${
              hasZeroTokens ? 'bg-red-100' : 'bg-yellow-100'
            }`}>
              <ExclamationTriangleIcon className={`h-8 w-8 ${
                hasZeroTokens ? 'text-red-600' : 'text-yellow-600'
              }`} />
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {hasZeroTokens ? 'No Tokens Available' : 'Low Token Balance'}
            </h3>
            
            <p className="text-sm text-gray-600 mb-4">
              {hasZeroTokens 
                ? "You don't have any tokens left. Please contact your administrator to add more tokens to continue using the chat features."
                : `You need at least ${minToken.toLocaleString()} tokens to continue using the platform. Your current balance is ${availableTokens.toLocaleString()} tokens.`
              }
            </p>
            
            <div className={`rounded-lg p-3 mb-4 ${
              hasZeroTokens ? 'bg-red-50' : 'bg-yellow-50'
            }`}>
              <div className="flex items-center justify-center space-x-2">
                <WalletIcon className={`h-4 w-4 ${
                  hasZeroTokens ? 'text-red-600' : 'text-yellow-600'
                }`} />
                <span className={`text-sm ${
                  hasZeroTokens ? 'text-red-700' : 'text-yellow-700'
                }`}>
                  Your balance: {availableTokens.toLocaleString()} tokens
                  {!hasZeroTokens && ` (Minimum required: ${minToken.toLocaleString()})`}
                </span>
              </div>
            </div>
            
            <button
              onClick={() => setShowNoTokenModal(false)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    );
  };

  console.log(selectedChatData);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* No Token Modal */}
      {showNoTokenModal && <NoTokenModal />}

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.pdf"
        onChange={(e) => handleFileSelect(e.target.files[0])}
        className="hidden"
      />

      {/* Left Sidebar - Chat History */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ x: -300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            className="w-80 border-r border-gray-200 bg-white flex flex-col"
          >
            <div className="flex-1 overflow-y-auto">
              <ChatHistory
                onSelectChat={handleSelectChat}
                chatload={chatload}
                setchatload={setchatload}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-2 py-1 sticky top-0 z-10 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {isFromHistory && (
                <button
                  onClick={() => {
                    setIsFromHistory(false);
                    setSelectedChatData(null);
                    setIsChatDisabled(false);
                    setShowChatbot(true);
                    resetAll();
                    toast.success('Back to new chat');
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="New Chat"
                >
                  <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}

              <div className="relative">
                <Listbox value={selectedModule} onChange={handleModuleChange} disabled={isFromHistory}>
                  <Listbox.Button className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${isFromHistory ? ' cursor-not-allowed opacity-60' : ' hover:bg-gray-100'
                    }`}>
                    <div className={`p-1.5 rounded-lg bg-${selectedModule.color}-100`}>
                      <selectedModule.icon className={`h-3 w-5 text-${selectedModule.color}-600`} />
                    </div>
                    <span className="font-small text-gray-700">{selectedModule.name}</span>
                    {!isFromHistory && <ChevronDownIcon className="h-3 w-5 text-gray-500" />}
                  </Listbox.Button>

                  {!isFromHistory && (
                    <Listbox.Options className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                      {modules.map((module) => (
                        <Listbox.Option
                          key={module.id}
                          value={module}
                          className={({ active }) =>
                            `px-4 py-3 cursor-pointer transition-colors ${active ? 'bg-gray-50' : ''}`
                          }
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-lg bg-${module.color}-100`}>
                              <module.icon className={`h-6 w-5 text-${module.color}-600`} />
                            </div>
                            <div>
                              <p className="font-small text-gray-900">{module.name}</p>
                            </div>
                          </div>
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  )}
                </Listbox>
              </div>
            </div>

            <div className="flex items-center space-x-3 text-sm text-gray-500">
              <div className={`flex items-center space-x-1 px-3 py-1.5 rounded-full ${
                availableTokens >= minToken ? 'bg-green-100' : 
                availableTokens > 0 ? 'bg-yellow-100' : 'bg-red-100'
              }`}>
                <WalletIcon className={`h-3.5 w-3.5 ${
                  availableTokens >= minToken ? 'text-green-600' : 
                  availableTokens > 0 ? 'text-yellow-600' : 'text-red-600'
                }`} />
                <span className={`text-xs font-medium ${
                  availableTokens >= minToken ? 'text-green-700' : 
                  availableTokens > 0 ? 'text-yellow-700' : 'text-red-700'
                }`}>
                  {availableTokens.toLocaleString()} tokens remaining
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="bg-white flex-1 overflow-y-auto">
          <div className="max-w-5xl mx-auto px-6 py-2">
            {/* Token Warning Banner - Show if tokens are below minimum */}
            {availableTokens < minToken && !isFromHistory && (
              <div className={`mb-6 p-4 rounded-lg ${
                availableTokens === 0 
                  ? 'bg-red-50 border border-red-200' 
                  : 'bg-yellow-50 border border-yellow-200'
              }`}>
                <div className="flex items-center space-x-3">
                  <ExclamationTriangleIcon className={`h-5 w-5 ${
                    availableTokens === 0 ? 'text-red-500' : 'text-yellow-500'
                  }`} />
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${
                      availableTokens === 0 ? 'text-red-700' : 'text-yellow-700'
                    }`}>
                      {availableTokens === 0 ? 'No tokens available' : 'Low token balance'}
                    </p>
                    <p className={`text-xs ${
                      availableTokens === 0 ? 'text-red-600' : 'text-yellow-600'
                    }`}>
                      {availableTokens === 0 
                        ? 'You have no tokens left. Please contact administrator to add more tokens.'
                        : `You need at least ${minToken.toLocaleString()} tokens to use the platform. Current balance: ${availableTokens.toLocaleString()} tokens.`
                      }
                    </p>
                  </div>
                  <button
                    onClick={() => setShowNoTokenModal(true)}
                    className={`px-3 py-1 text-white text-xs rounded-lg transition-colors ${
                      availableTokens === 0 ? 'bg-red-600 hover:bg-red-700' : 'bg-yellow-600 hover:bg-yellow-700'
                    }`}
                  >
                    Learn More
                  </button>
                </div>
              </div>
            )}

            {/* Tool instructions / explanation (stays visible before AND after upload) */}
            {!isFromHistory && availableTokens >= minToken && (
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 mb-6">
                <h3 className="text-base font-semibold text-gray-900 mb-2">How this tool works</h3>
                <p className="text-sm text-gray-700 leading-relaxed mb-2">
                  Every year many transplantable deceased-donor kidneys go unused &mdash; roughly 28% are
                  discarded &mdash; largely because of inefficiencies in the waitlist matching process. A
                  kidney that stays unmatched loses viability quickly: once it has gone unaccepted for more
                  than about 12 hours, its quality and the chance to save a life decline rapidly, and the
                  organ is ultimately discarded.
                </p>
                <p className="text-sm text-gray-700 leading-relaxed mb-4">
                  This tool provides access to our cutting-edge models that predict whether an organ will
                  be accepted within 100 offers if it is placed through the existing waitlist system. If an organ is unlikely to
                  be accepted on the waitlist, it is better to place it off-sequence to improve utilization
                  rates.
                </p>
                <p className="text-sm font-semibold text-gray-900 mb-2">To use this tool, follow these steps:</p>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 leading-relaxed">
                  <li>
                    <span className="font-medium">Upload kidney case data.</span> We accept CSV files, as
                    well as a PDF downloaded from DonorNet.
                  </li>
                  <li>
                    <span className="font-medium">Explore questions using the chat.</span> Ask the assistant
                    to explain the CART model, ask about the effect of various labs, or run a
                    &ldquo;what-if&rdquo; analysis (e.g. &ldquo;what if the organ&rsquo;s creatinine were
                    lowered to 0.5?&rdquo;). You can also ask for the specific contribution of individual
                    fields to the predicted likelihood that the organ is accepted within 100 offers.
                  </li>
                  <li>
                    <span className="font-medium">Give us your feedback.</span> Tell the assistant whether
                    you agreed with the predictions, and how you would change the analysis. This feedback is
                    incorporated into the tool, with attribution to your contribution.
                  </li>
                </ol>
                <p className="text-xs text-gray-500 mt-4">
                  We do not save any identifiable patient information.
                </p>
              </div>
            )}

            {/* Only show upload section when NOT viewing history chat AND has sufficient tokens */}
            {!isFromHistory && !uploadedFile && !uploading && availableTokens >= minToken && (
              <div className="bg-gray-50 mb-6">
                <div
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                    }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={handleFileIconClick}
                >
                  <DocumentArrowUpIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600 mb-1">Click to upload or drag &amp; drop a CSV file or DonorNet PDF</p>
                  <p className="text-xs text-gray-400">CSV or DonorNet PDF (max 10MB)</p>
                </div>

                {/* Sample cases — load pre-existing data to try the model */}
                <div className="mt-3 flex flex-wrap items-center justify-center gap-3 text-sm">
                  <span className="text-gray-500">Or try the model with a sample organ:</span>
                  {sampleOrgans.map((sample) => (
                    <button
                      key={sample.path}
                      onClick={() => loadSample(sample.path, sample.file)}
                      className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-white hover:border-blue-400 transition-colors"
                    >
                      {sample.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Show message when insufficient tokens */}
            {!isFromHistory && !uploadedFile && !uploading && availableTokens < minToken && (
              <div className={`rounded-xl p-12 text-center mb-6 ${
                availableTokens === 0 ? 'bg-red-50' : 'bg-yellow-50'
              }`}>
                <ExclamationTriangleIcon className={`h-16 w-16 mx-auto mb-4 ${
                  availableTokens === 0 ? 'text-red-400' : 'text-yellow-400'
                }`} />
                <h3 className={`text-lg font-medium mb-2 ${
                  availableTokens === 0 ? 'text-red-700' : 'text-yellow-700'
                }`}>
                  {availableTokens === 0 ? 'No Tokens Available' : 'Insufficient Tokens'}
                </h3>
                <p className={`text-sm mb-4 ${
                  availableTokens === 0 ? 'text-red-600' : 'text-yellow-600'
                }`}>
                  {availableTokens === 0 
                    ? "You don't have any tokens to upload files or use the chat features."
                    : `You need at least ${minToken.toLocaleString()} tokens to use the platform. Your current balance is ${availableTokens.toLocaleString()} tokens.`
                  }
                </p>
                <button
                  onClick={() => setShowNoTokenModal(true)}
                  className={`px-4 py-2 text-white rounded-lg transition-colors ${
                    availableTokens === 0 ? 'bg-red-600 hover:bg-red-700' : 'bg-yellow-600 hover:bg-yellow-700'
                  }`}
                >
                  Learn More
                </button>
              </div>
            )}

            {/* File Details Section - Modern Design */}
            {selectedChatData?.file_data?.files && selectedChatData.file_data.files.length > 0 && (
              <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-sm font-semibold text-gray-800">
                        Model : {selectedChatData?.messages[0]?.module}
                      </h3>
                      <span className="text-xs text-gray-500">
                        Total Size: {selectedChatData.file_data.files.reduce((total, file) => total + (file.file_size || 0), 0).toLocaleString()} bytes
                      </span>
                    </div>
                    {selectedChatData.file_data.created_at && (
                      <span className="text-xs text-gray-400">
                        Uploaded: {new Date(selectedChatData.file_data.created_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Files List */}
                <div className="divide-y divide-gray-100">
                  {selectedChatData.file_data.files.map((file, index) => (
                    <div key={file.file_id || file.id || index} className="p-4 hover:bg-gray-50 transition-colors duration-150">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          {/* File Icon based on type */}
                          <div className="flex-shrink-0">
                            {file.mime_type?.startsWith('image/') ? (
                              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            ) : file.mime_type === 'application/pdf' ? (
                              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                              </div>
                            ) : file.mime_type?.startsWith('text/') || file.file_extension === '.csv' ? (
                              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                            ) : (
                              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                                <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                            )}
                          </div>

                          {/* File Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {file.original_name || file.filename}
                              </p>
                              {file.status && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${file.status === 'UPLOADED' || file.status === 'completed'
                                  ? 'bg-green-100 text-green-800'
                                  : file.status === 'processing'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                                  }`}>
                                  {file.status}
                                </span>
                              )}
                            </div>

                            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                              {file.file_size && (
                                <span className="flex items-center space-x-1">
                                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7C5 4 4 5 4 7z" />
                                  </svg>
                                  <span>{file.file_size_formatted || formatFileSize(file.file_size)}</span>
                                </span>
                              )}

                              {file.mime_type && (
                                <span className="flex items-center space-x-1">
                                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                                  </svg>
                                  <span>{file.mime_type}</span>
                                </span>
                              )}

                              {file.file_extension && (
                                <span className="flex items-center space-x-1">
                                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                                  </svg>
                                  <span>{file.file_extension}</span>
                                </span>
                              )}

                              {file.uploaded_at && (
                                <span className="flex items-center space-x-1">
                                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  <span>{new Date(file.uploaded_at).toLocaleString()}</span>
                                </span>
                              )}
                            </div>

                            {/* File ID Badge */}
                            {file.file_id && (
                              <div className="mt-1">
                                <span className="text-xs text-gray-400">File ID: {file.file_id}</span>
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center space-x-1 flex-shrink-0" style={{ background: "", border: "1px solid #ddd", padding: "10px", borderRadius: "5px" }}>
                            <button
                              onClick={() => {
                                setIsFromHistory(false);
                                setSelectedChatData(null);
                                setIsChatDisabled(false);
                                setShowChatbot(true);
                                resetAll();
                              }}
                              className="text-xs text-blue-600 hover:text-blue-700"
                            >
                              Start New Chat
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {uploading && (
              <div className="bg-gray-50 rounded-xl p-4 mb-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-700">Uploading...</span>
                  <span className="text-sm font-medium text-blue-600">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* File Info - Show for both new uploads and history */}
            {uploadedFile && (
              <div className="mb-6 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <ClockIcon className="h-5 w-5 text-blue-500" />
                    <span className="text-sm text-blue-700">
                      Viewing : {uploadedFile.name}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setIsFromHistory(false);
                      setSelectedChatData(null);
                      setIsChatDisabled(false);
                      setShowChatbot(true);
                      resetAll();
                    }}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    Start New Chat
                  </button>
                </div>
              </div>
            )}

            {/* Data Preview - Show for both new uploads and history */}
            {uploadedData && uploadedData.rows && uploadedData.rows.length > 0 && (
              <div className="mb-6">
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                  <div
                    className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => setShowDataPreview(!showDataPreview)}
                  >
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                      <TableCellsIcon className="h-4 w-4 mr-2 text-blue-500" />
                      Data Preview ({uploadedData.totalRows} rows, {uploadedData.totalColumns} columns)
                    </h3>
                    {showDataPreview ? (
                      <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                    )}
                  </div>

                  {showDataPreview && (
                    <div className="p-0">
                      <div className="overflow-auto max-h-64">
                        <table className="w-full divide-y divide-gray-200 text-xs">
                          <thead className="bg-gray-100 sticky top-0">
                            <tr>
                              {uploadedData.headers.map((header, idx) => (
                                <th key={idx} className="px-3 py-2 text-left font-medium text-gray-700">
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {paginatedRows.map((row, rowIdx) => (
                              <tr key={rowIdx} className="hover:bg-gray-50">
                                {uploadedData.headers.map((header, colIdx) => (
                                  <td key={colIdx} className="px-3 py-2 text-gray-600 truncate max-w-[200px]">
                                    {row[header] || '-'}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {totalPages > 1 && (
                        <div className="flex justify-between items-center px-4 py-2 border-t border-gray-200 bg-gray-50">
                          <p className="text-xs text-gray-500">Page {currentPage} of {totalPages}</p>
                          <div className="flex space-x-1">
                            <button
                              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                              disabled={currentPage === 1}
                              className="p-1 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                            >
                              <ChevronLeftIcon className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                              disabled={currentPage === totalPages}
                              className="p-1 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                            >
                              <ChevronRightIcon className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Run Module Button - Only for new uploads, not history, and has sufficient tokens */}
            {!isFromHistory && uploadedFile && !moduleResult && !processing && availableTokens >= minToken && (
              <div className="mb-6">
                <button
                  onClick={handleRunModule}
                  className="w-full py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg hover:from-blue-700 hover:to-blue-600 disabled:opacity-50 flex items-center justify-center transition-all shadow-sm text-sm"
                >
                  <PlayIcon className="h-4 w-4 mr-2" />
                  Run Model
                </button>
              </div>
            )}

            {processing && !moduleResult && (
              <div className="mb-6 flex items-center justify-center space-x-2 py-2">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-blue-600"></div>
                <span className="text-sm text-gray-600">Processing with {selectedModule.name}...</span>
              </div>
            )}

            {/* Result Display - Show for both new uploads and history */}
            {moduleResult && (
              <div className="mb-6">
                <ResultDisplay
                  result={moduleResult.data}
                  moduleName={selectedModule.name}
                  moduleColor={selectedModule.color}
                  moduleId={selectedModule.id}
                />
              </div>
            )}

            {/* Full chatbot UI for a live run, and for a resumed conversation */}
            {(!isFromHistory || isResumedHistory) && showChatbot && hasChatContent && (
              <div className="mt-6">
                <div className="border-t border-gray-200 pt-4 mb-4">
                  <h2 className="text-sm font-medium text-gray-700 flex items-center">
                    <CpuChipIcon className="h-4 w-4 mr-2 text-blue-500" />
                    Chatbot
                  </h2>
                </div>

                {/* Backend Tool Logs Accordion */}
                <div className="border border-gray-200 rounded-xl overflow-hidden mb-4">
                  <button
                    onClick={() => setOpenAccordion(openAccordion === 1 ? null : 1)}
                    className="w-full flex justify-between items-center px-4 py-3 bg-gray-50 hover:bg-gray-100 transition"
                  >
                    <span className="text-sm font-medium text-gray-700 flex items-center">
                      <CodeBracketIcon className="h-4 w-4 mr-2 text-blue-500" />
                      📊 Backend Tool Logs
                      {toolLogs.length > 0 && (
                        <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                          {toolLogs.length}
                        </span>
                      )}
                    </span>
                    <ChevronDownIcon
                      className={`h-4 w-4 transition-transform ${openAccordion === 1 ? "rotate-180" : ""
                        }`}
                    />
                  </button>

                  <AnimatePresence>
                    {openAccordion === 1 && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-3 bg-white max-h-96 overflow-y-auto"
                      >
                        {toolLogs.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <CodeBracketIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                            <p className="text-sm">No tool logs yet</p>
                            <p className="text-xs">Tool logs will appear here when you interact with the chatbot</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {toolLogs.map((log, index) => (
                              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                                  <div className="flex items-center space-x-2">
                                    <DocumentTextIcon className="h-4 w-4 text-blue-500" />
                                    <span className="text-xs font-medium text-gray-700">
                                      Tool: {log.tool_name || 'Unknown Tool'}
                                    </span>
                                  </div>
                                  <span className="text-xs text-gray-500">
                                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : new Date().toLocaleString()}
                                  </span>
                                </div>

                                <div className="p-4 space-y-3">
                                  {/* Arguments */}
                                  {log.parsed_arguments && (
                                    <div>
                                      <h4 className="text-xs font-semibold text-gray-600 mb-1">Arguments:</h4>
                                      <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                                        {JSON.stringify(log.parsed_arguments, null, 2)}
                                      </pre>
                                    </div>
                                  )}

                                  {/* Output */}
                                  {log.output && (
                                    <div>
                                      <h4 className="text-xs font-semibold text-gray-600 mb-1">Output:</h4>
                                      <div className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                                        {(() => {
                                          try {
                                            const parsedOutput = JSON.parse(log.output);
                                            return (
                                              <div className="space-y-2">
                                                {parsedOutput.predictions && (
                                                  <div>
                                                    <div className="font-semibold text-gray-700 mb-1">Predictions:</div>
                                                    {Object.entries(parsedOutput.predictions).map(([model, value]) => (
                                                      <div key={model} className="ml-2 mb-1">
                                                        <span className="text-gray-600">{model}:</span>
                                                        <span className="ml-2 font-mono">
                                                          {typeof value === 'object' ? value.probability?.toFixed(6) || JSON.stringify(value) : value?.toFixed(6)}
                                                        </span>
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                                {parsedOutput.applied_adjustments && (
                                                  <div>
                                                    <div className="font-semibold text-gray-700 mb-1">Applied Adjustments:</div>
                                                    {parsedOutput.applied_adjustments.map((adj, idx) => (
                                                      <div key={idx} className="ml-2 text-gray-600">
                                                        {adj.feature}: {adj.value}
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                                {parsedOutput.warnings && parsedOutput.warnings.length > 0 && (
                                                  <div>
                                                    <div className="font-semibold text-yellow-600 mb-1">Warnings:</div>
                                                    {parsedOutput.warnings.map((warning, idx) => (
                                                      <div key={idx} className="ml-2 text-yellow-600 text-xs">
                                                        ⚠️ {warning}
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          } catch {
                                            return <pre className="text-xs">{log.output}</pre>;
                                          }
                                        })()}
                                      </div>
                                    </div>
                                  )}

                                  {/* Tool Call ID */}
                                  {log.tool_call_id && (
                                    <div className="text-xs text-gray-400">
                                      Tool Call ID: {log.tool_call_id}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div className="space-y-4">
                  <AnimatePresence>
                    {messages.map((message, index) => (
                      <MessageBubble
                        key={message.id || index}
                        message={message}
                        isUser={message.isUser}
                        isTyping={message.isTyping || false}
                      />
                    ))}
                  </AnimatePresence>

                  {isTyping && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-start space-x-3"
                    >
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-gradient-to-br from-gray-500 to-gray-600 rounded-full flex items-center justify-center">
                          <UserIcon className="h-4 w-4 text-white" />
                        </div>
                      </div>
                      <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>

                <div ref={messagesEndRef} />
              </div>
            )}

            {/* Read-only transcript: history that cannot be resumed */}
            {isFromHistory && !isResumedHistory && messages.length > 0 && (
              <div className="mt-6">
                <div className="space-y-4">
                  <AnimatePresence>
                    {messages.map((message, index) => (
                      <MessageBubble
                        key={message.id || index}
                        message={message}
                        isUser={message.isUser}
                      />
                    ))}
                  </AnimatePresence>
                </div>
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Input Area - live runs and resumed conversations, tokens permitting */}
        {(!isFromHistory || isResumedHistory) && showChatbot && hasChatContent && availableTokens >= minToken && (
          <div className="bg-white border-t border-gray-200 px-6 py-4 flex-shrink-0">
            <div className="max-w-5xl mx-auto">
              <form onSubmit={handleSend} className="flex items-center space-x-3">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about the analysis results..."
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                  disabled={isSending}
                />

                <button
                  type="submit"
                  disabled={!input.trim() || isSending}
                  className="bg-blue-600 text-white px-5 py-3 rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center space-x-2 shadow-sm"
                >
                  {isSending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <PaperAirplaneIcon className="h-4 w-4" />
                  )}
                  <span className="text-sm font-medium">Send</span>
                </button>
              </form>
              <p className="text-xs text-center text-gray-400 mt-2">
                Ask questions about your data analysis results
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatInterface;